// CalendrierV2 — Calendrier unique de toute l'activité de la communication.
//  Agrège : agenda (ep:events), tâches, publications, réunions, campagnes,
//  demandes clubs, Metricool, + tournois (API externe, lecture seule) et
//  événements ponctuels comm (ep:commevents).
//
//  Droits par rôle :
//   • Admin (user.admin === true) : voit tout, tous clubs, filtre multi-clubs.
//   • Non-admin : voit uniquement ses clubs (user.clubs) + les éléments
//     nationaux (sans club, ou club « France »). La restriction s'applique
//     aussi aux fiches détaillées.
//   • Un élément multi-clubs apparaît pour chacun de ses clubs + en vue globale.
//
//  Couleurs par type · filtres type / club / personne · vues mois / semaine /
//  jour / liste · fiche détaillée au clic · création directe (événement
//  ponctuel, tâche, réunion, publication) selon les droits.

import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  useCalendarEvents, useTasks, usePublications,
  useMeetings, useMetricool, useClubs,
  useCampagnes, useRequests, useUsers,
  useCommEvents, useEventEditors,
} from "../hooks/useV1Data";
import {
  saveCommEvent, deleteCommEvent, createTask,
  createMeeting, createPublication, toggleEventEditor,
} from "../hooks/useCalendarWrite";
import {
  C, SOURCE_LABELS, FILTER_ORDER, ROUTE_MAP, FR_DAYS, FR_MONTHS,
  startOfDay, addDays, addMonths, isSameDay, fmtISO, fmtDayFull,
  parseDate, getWeekStart, getMonthGridDates, itemCoversDay,
  resolveUser, resolveUserList,
  normalizeEvents, normalizeTasks, normalizePublications, normalizeMeetings,
  normalizeMetricool, normalizeCampagnes, normalizeRequests, normalizeTournaments, normalizeCommEvents,
  visibleToUser, applyFilters,
  HOUR_START, HOUR_END, HOUR_H, HOURS, timeToMins,
} from "../lib/calendarItems";

/* ════════════════════════════════════════════════════════
   ROOT COMPONENT
   (moteur d'agrégation + droits : ../lib/calendarItems)
   ════════════════════════════════════════════════════════ */
export default function CalendrierV2({ appId, currentUser, onNavigate }) {
  const { events }       = useCalendarEvents();
  const { tasks }        = useTasks();
  const { publications } = usePublications();
  const { meetings }     = useMeetings();
  const { metricool }    = useMetricool();
  const { campagnes }    = useCampagnes();
  const { requests }     = useRequests();
  const { clubs }        = useClubs();
  const { users }        = useUsers();
  const { commEvents }   = useCommEvents();
  const { eventEditors } = useEventEditors();

  const [tournaments, setTournaments] = useState([]);
  const [tErr, setTErr] = useState(false);

  const [view,            setView]           = useState("month");
  const [focusDate,       setFocusDate]      = useState(() => startOfDay(new Date()));
  const [miniMonth,       setMiniMonth]      = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [selectedClubs,   setSelectedClubs]  = useState(new Set());
  const [selectedSources, setSelectedSources]= useState(new Set());
  const [selectedPeople,  setSelectedPeople] = useState(new Set());
  const [popover,         setPopover]        = useState(null);
  const [createOpen,      setCreateOpen]     = useState(false);
  const [editEvent,       setEditEvent]      = useState(null);  // commevent en édition
  const [permOpen,        setPermOpen]       = useState(false);
  const popoverRef = useRef(null);

  const isAdmin     = currentUser?.admin === true;
  const userClubIds = useMemo(
    () => new Set((currentUser?.clubs || []).map(String)),
    [currentUser]
  );
  const isEventEditor = isAdmin || (Array.isArray(eventEditors) && eventEditors.map(String).includes(String(appId)));
  const canCreate = !!currentUser; // membres de l'équipe connectés

  // Tournois (tous clubs) — API externe, lecture seule
  useEffect(() => {
    let alive = true;
    fetch("/api/events/tournaments", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!alive) return; setTournaments((j && j.tournaments) || []); setTErr(!!(j && j.error)); })
      .catch(() => { if (alive) setTErr(true); });
    return () => { alive = false; };
  }, []);

  const allItems = useMemo(() => {
    const items = [
      ...normalizeTournaments(tournaments, clubs),
      ...normalizeEvents(events, clubs, users),
      ...normalizeCommEvents(commEvents, clubs, users),
      ...normalizePublications(publications, clubs, users),
      ...normalizeMeetings(meetings, clubs, users),
      ...normalizeTasks(tasks, clubs, users),
      ...normalizeCampagnes(campagnes, clubs, users),
      ...normalizeRequests(requests, clubs, users),
      ...normalizeMetricool(metricool, clubs, users),
    ];
    return items.filter(it => visibleToUser(it, { isAdmin, userClubIds, appId }));
  }, [tournaments, events, commEvents, publications, meetings, tasks, campagnes, requests, metricool, clubs, users, isAdmin, userClubIds, appId]);

  const filtered = useMemo(
    () => applyFilters(allItems, selectedClubs, selectedSources, selectedPeople),
    [allItems, selectedClubs, selectedSources, selectedPeople]
  );

  // Options du filtre « Personne » (union des personnes des items visibles)
  const peopleOptions = useMemo(() => {
    const s = new Set();
    allItems.forEach(it => (it.people || []).forEach(p => s.add(p)));
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [allItems]);

  // Clubs affichables dans le filtre latéral (admin = tous ; sinon = ses clubs)
  const clubList = useMemo(() => {
    const base = clubs.length > 0 ? clubs : [];
    return isAdmin ? base : base.filter(c => userClubIds.has(String(c.id)));
  }, [clubs, isAdmin, userClubIds]);

  useEffect(() => {
    if (!popover) return;
    function close(e) {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) setPopover(null);
    }
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [popover]);

  function toggleSet(setter, value) {
    setter(prev => {
      const next = new Set(prev);
      next.has(value) ? next.delete(value) : next.add(value);
      return next;
    });
  }

  function navPrev() {
    if (view === "week")  setFocusDate(d => addDays(d, -7));
    if (view === "day")   setFocusDate(d => addDays(d, -1));
    if (view === "month" || view === "list") setFocusDate(d => addMonths(d, -1));
  }
  function navNext() {
    if (view === "week")  setFocusDate(d => addDays(d, 7));
    if (view === "day")   setFocusDate(d => addDays(d, 1));
    if (view === "month" || view === "list") setFocusDate(d => addMonths(d, 1));
  }
  function navToday() {
    const t = startOfDay(new Date());
    setFocusDate(t);
    setMiniMonth(new Date(t.getFullYear(), t.getMonth(), 1));
  }

  function openPopover(item, e) {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setPopover({ item, x: rect.left, y: rect.bottom + 8 });
  }

  function headerTitle() {
    if (view === "month" || view === "list") return `${FR_MONTHS[focusDate.getMonth()]} ${focusDate.getFullYear()}`;
    if (view === "day")   return `${FR_DAYS[(focusDate.getDay() + 6) % 7]} ${focusDate.getDate()} ${FR_MONTHS[focusDate.getMonth()]}`;
    const ws = getWeekStart(focusDate), we = addDays(ws, 6);
    if (ws.getMonth() === we.getMonth()) return `${FR_MONTHS[ws.getMonth()]} ${ws.getFullYear()}`;
    return `${FR_MONTHS[ws.getMonth()].slice(0,3)} – ${FR_MONTHS[we.getMonth()].slice(0,3)} ${we.getFullYear()}`;
  }

  async function handleSaveCommEvent(form) {
    await saveCommEvent(commEvents, form, appId, currentUser);
    setEditEvent(null);
    setCreateOpen(false);
  }
  async function handleDeleteCommEvent(id) {
    if (!window.confirm("Supprimer cet événement ?")) return;
    await deleteCommEvent(commEvents, id);
    setEditEvent(null);
  }

  return (
    <div className="cal-root">
      {/* ── Left panel ── */}
      <aside className="cal-aside">
        <MiniCalendar
          month={miniMonth}
          focusDate={focusDate}
          items={filtered}
          onNavigate={d => { setFocusDate(startOfDay(d)); if (view === "month" || view === "list") setView("day"); }}
          onMonthChange={setMiniMonth}
        />

        {clubList.length > 1 && (
          <div className="cal-filter-section">
            <div className="cal-filter-label">Clubs</div>
            {clubList.map(club => (
              <label key={club.id} className="cal-filter-item">
                <input type="checkbox" checked={selectedClubs.has(String(club.id))} onChange={() => toggleSet(setSelectedClubs, String(club.id))} />
                <span className="cal-filter-dot" style={{ background: club.color || "var(--text-3)" }} />
                <span>{club.name || club.nom}</span>
              </label>
            ))}
          </div>
        )}

        <div className="cal-filter-section">
          <div className="cal-filter-label">Types</div>
          {FILTER_ORDER.map(src => (
            <label key={src} className="cal-filter-item">
              <input type="checkbox" checked={selectedSources.has(src)} onChange={() => toggleSet(setSelectedSources, src)} />
              <span className="cal-filter-dot" style={{ background: C[src].bg, border: `1.5px solid ${C[src].border}` }} />
              <span>{SOURCE_LABELS[src]}</span>
            </label>
          ))}
          <div className="cal-filter-item cal-filter-disconnected">
            <span style={{ width: 14, flexShrink: 0 }} />
            <span className="cal-filter-dot" style={{ background: "#E5E7EB", border: "1.5px solid #D1D5DB" }} />
            <span>{SOURCE_LABELS.marketing360}</span>
            <span className="cal-filter-badge">Bientôt</span>
          </div>
        </div>

        {peopleOptions.length > 0 && (
          <div className="cal-filter-section">
            <div className="cal-filter-label">Personnes</div>
            <div className="cal-filter-people">
              {peopleOptions.map(p => (
                <label key={p} className="cal-filter-item">
                  <input type="checkbox" checked={selectedPeople.has(p)} onChange={() => toggleSet(setSelectedPeople, p)} />
                  <span>{p}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </aside>

      {/* ── Main ── */}
      <div className="cal-main">
        <div className="cal-toolbar">
          <div className="cal-toolbar-left">
            <div className="cal-nav">
              <button className="cal-nav-btn" onClick={navPrev} aria-label="Précédent">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
              <button className="cal-nav-btn" onClick={navNext} aria-label="Suivant">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
            </div>
            <h2 className="cal-period-title">{headerTitle()}</h2>
            <button className="cal-btn-today" onClick={navToday}>Aujourd'hui</button>
          </div>
          <div className="cal-toolbar-right">
            <div className="cal-view-tabs">
              {[["day","Jour"],["week","Semaine"],["month","Mois"],["list","Liste"]].map(([v, label]) => (
                <button key={v} className={`cal-view-tab${view === v ? " active" : ""}`} onClick={() => setView(v)}>{label}</button>
              ))}
            </div>
            {isAdmin && (
              <button className="cal-btn-ghost" onClick={() => setPermOpen(true)} title="Autorisations événements">⚙️</button>
            )}
            {canCreate && (
              <button className="cal-btn-create" onClick={() => setCreateOpen(true)}>+ Créer</button>
            )}
          </div>
        </div>

        {tErr && (
          <div className="cal-banner">⚠️ Les tournois n'ont pas pu être chargés (source momentanément indisponible). Le reste du calendrier reste visible.</div>
        )}

        <div className="cal-body">
          {view === "week"  && <WeekView  focusDate={focusDate} items={filtered} onItemClick={openPopover} onDayClick={d => { setFocusDate(d); setView("day"); }} />}
          {view === "month" && <MonthView focusDate={focusDate} items={filtered} onItemClick={openPopover} onDayClick={d => { setFocusDate(d); setView("day"); }} />}
          {view === "day"   && <DayView   focusDate={focusDate} items={filtered} onItemClick={openPopover} />}
          {view === "list"  && <ListView  focusDate={focusDate} items={filtered} onItemClick={openPopover} />}
        </div>
      </div>

      {popover && (
        <ItemPopover
          ref={popoverRef} item={popover.item} x={popover.x} y={popover.y} users={users} campagnes={campagnes}
          onNavigate={onNavigate}
          onEditCommEvent={(raw) => { setPopover(null); setEditEvent(raw); }}
          canEditCommEvent={isEventEditor}
          onClose={() => setPopover(null)}
        />
      )}

      {createOpen && (
        <CreateModal
          clubs={clubList} users={users} campagnes={campagnes} isAdmin={isAdmin}
          defaultClubs={isAdmin ? [] : [...userClubIds]}
          defaultDate={fmtISO(focusDate)}
          canCreateEvent={isEventEditor}
          onClose={() => setCreateOpen(false)}
          onCreateCommEvent={handleSaveCommEvent}
          onCreateTask={async (f) => { await createTask(tasks, f, appId); setCreateOpen(false); }}
          onCreateMeeting={async (f) => { await createMeeting(meetings, f, appId); setCreateOpen(false); }}
          onCreatePublication={async (f) => { await createPublication(publications, f, appId); setCreateOpen(false); }}
        />
      )}

      {editEvent && (
        <EventModal
          initial={editEvent} clubs={clubList}
          onClose={() => setEditEvent(null)}
          onSave={handleSaveCommEvent}
          onDelete={handleDeleteCommEvent}
          canDelete={!!editEvent.id}
        />
      )}

      {permOpen && (
        <PermModal
          users={users} editors={eventEditors || []}
          onToggle={(uid) => toggleEventEditor(eventEditors, uid)}
          onClose={() => setPermOpen(false)}
        />
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   MINI CALENDAR
   ════════════════════════════════════════════════════════ */
function MiniCalendar({ month, focusDate, items, onNavigate, onMonthChange }) {
  const year = month.getFullYear(), mon = month.getMonth();
  const dates = getMonthGridDates(year, mon);

  const busySet = useMemo(() => {
    const s = new Set();
    dates.forEach(d => { if (items.some(item => itemCoversDay(item, d))) s.add(fmtISO(d)); });
    return s;
  }, [items, dates]);

  return (
    <div className="cal-mini">
      <div className="cal-mini-header">
        <button className="cal-mini-nav" onClick={() => onMonthChange(addMonths(month, -1))}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 11L5 7l4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
        <span className="cal-mini-title">{FR_MONTHS[mon]} {year}</span>
        <button className="cal-mini-nav" onClick={() => onMonthChange(addMonths(month, 1))}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
      </div>
      <div className="cal-mini-grid">
        {FR_DAYS.map(d => <div key={d} className="cal-mini-dow">{d.slice(0,2)}</div>)}
        {dates.map((d, i) => (
          <button
            key={i}
            className={`cal-mini-day${isSameDay(d, new Date()) ? " today" : ""}${isSameDay(d, focusDate) ? " focus" : ""}${d.getMonth() !== mon ? " other" : ""}`}
            onClick={() => onNavigate(d)}
          >
            {d.getDate()}
            {busySet.has(fmtISO(d)) && !isSameDay(d, focusDate) && <span className="cal-mini-dot" />}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   WEEK VIEW
   ════════════════════════════════════════════════════════ */
function WeekView({ focusDate, items, onItemClick, onDayClick }) {
  const weekStart = getWeekStart(focusDate);
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const timed = useMemo(() => items.filter(x => !x.allDay && x.time), [items]);

  return (
    <div className="cal-week">
      <div className="cal-week-header">
        <div className="cal-gutter" />
        {days.map((d, i) => {
          const isT = isSameDay(d, new Date());
          return (
            <div key={i} className={`cal-week-dh${isT ? " today" : ""}`}>
              <button className="cal-week-dh-btn" onClick={() => onDayClick(d)}>
                <span className="cal-week-dow">{FR_DAYS[i]}</span>
                <span className={`cal-week-dnum${isT ? " today" : ""}`}>{d.getDate()}</span>
              </button>
            </div>
          );
        })}
      </div>

      <div className="cal-allday-row">
        <div className="cal-gutter cal-allday-lbl">Journée</div>
        {days.map((d, i) => {
          const dayAllDay = items.filter(x => x.allDay && itemCoversDay(x, d));
          return (
            <div key={i} className="cal-allday-cell">
              {dayAllDay.map(item => (
                <button key={`${item.id}-${fmtISO(d)}`} className="cal-chip" style={{ background: item.color.bg, color: item.color.text, borderColor: item.color.border }} onClick={e => onItemClick(item, e)}>
                  {item.title}
                </button>
              ))}
            </div>
          );
        })}
      </div>

      <div className="cal-grid-wrap">
        <div className="cal-grid">
          <div className="cal-hours-col">
            {HOURS.map(h => <div key={h} className="cal-hour-lbl">{h}h</div>)}
          </div>
          {days.map((d, di) => {
            const isT = isSameDay(d, new Date());
            const dayTimed = timed.filter(x => isSameDay(x.date, d));
            return (
              <div key={di} className={`cal-day-col${isT ? " today" : ""}`}>
                {HOURS.map(h => <div key={h} className="cal-hline" style={{ top: (h - HOUR_START) * HOUR_H }} />)}
                {dayTimed.map(item => <TimedEvent key={item.id} item={item} onClick={e => onItemClick(item, e)} />)}
                {isT && <NowLine />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   MONTH VIEW
   ════════════════════════════════════════════════════════ */
function MonthView({ focusDate, items, onItemClick, onDayClick }) {
  const year = focusDate.getFullYear(), month = focusDate.getMonth();
  const dates = getMonthGridDates(year, month);

  return (
    <div className="cal-month">
      <div className="cal-month-header">
        {FR_DAYS.map(d => <div key={d} className="cal-month-dow">{d}</div>)}
      </div>
      <div className="cal-month-grid">
        {dates.map((d, i) => {
          const isT = isSameDay(d, new Date()), isF = isSameDay(d, focusDate), other = d.getMonth() !== month;
          const dayTimed  = items.filter(x => !x.allDay && isSameDay(x.date, d));
          const dayAllDay = items.filter(x => x.allDay && itemCoversDay(x, d));
          const dayItems  = [...dayAllDay, ...dayTimed];
          const shown = dayItems.slice(0, 3), more = dayItems.length - shown.length;
          return (
            <div key={i} className={`cal-mcell${other ? " other" : ""}${isT ? " today" : ""}`} onClick={() => onDayClick(d)}>
              <div className={`cal-mnum${isT ? " today" : ""}${isF && !isT ? " focus" : ""}`}>{d.getDate()}</div>
              <div className="cal-mevents">
                {shown.map(item => (
                  <button key={`${item.id}-${fmtISO(d)}`} className="cal-mchip" style={{ background: item.color.bg, color: item.color.text, borderLeft: `2.5px solid ${item.color.border}` }} onClick={e => { e.stopPropagation(); onItemClick(item, e); }}>
                    {item.title}
                  </button>
                ))}
                {more > 0 && <div className="cal-mmore">+{more} autre{more > 1 ? "s" : ""}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   DAY VIEW
   ════════════════════════════════════════════════════════ */
function DayView({ focusDate, items, onItemClick }) {
  const allDay = useMemo(() => items.filter(x => x.allDay && itemCoversDay(x, focusDate)), [items, focusDate]);
  const timed  = useMemo(() => items.filter(x => !x.allDay && x.time && isSameDay(x.date, focusDate)), [items, focusDate]);
  const isT    = isSameDay(focusDate, new Date());

  return (
    <div className="cal-day">
      {allDay.length > 0 && (
        <div className="cal-day-allday">
          <div className="cal-gutter cal-allday-lbl">Journée</div>
          <div className="cal-day-allday-chips">
            {allDay.map(item => (
              <button key={item.id} className="cal-chip" style={{ background: item.color.bg, color: item.color.text, borderColor: item.color.border }} onClick={e => onItemClick(item, e)}>
                {item.title}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="cal-grid-wrap">
        <div className="cal-grid" style={{ gridTemplateColumns: "48px 1fr" }}>
          <div className="cal-hours-col">
            {HOURS.map(h => <div key={h} className="cal-hour-lbl">{h}h</div>)}
          </div>
          <div className={`cal-day-col${isT ? " today" : ""}`} style={{ borderLeft: "1px solid var(--border)" }}>
            {HOURS.map(h => <div key={h} className="cal-hline" style={{ top: (h - HOUR_START) * HOUR_H }} />)}
            {timed.map(item => <TimedEvent key={item.id} item={item} onClick={e => onItemClick(item, e)} />)}
            {isT && <NowLine />}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   LIST VIEW — éléments du mois, groupés par jour
   ════════════════════════════════════════════════════════ */
function ListView({ focusDate, items, onItemClick }) {
  const groups = useMemo(() => {
    const y = focusDate.getFullYear(), m = focusDate.getMonth();
    const inMonth = items
      .filter(it => it.date && it.date.getFullYear() === y && it.date.getMonth() === m)
      .sort((a, b) => a.date - b.date || String(a.time || "").localeCompare(String(b.time || "")));
    const map = new Map();
    for (const it of inMonth) {
      const key = fmtISO(it.date);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(it);
    }
    return [...map.entries()];
  }, [items, focusDate]);

  if (groups.length === 0) {
    return <div className="cal-list-empty">Aucun élément ce mois-ci.</div>;
  }

  return (
    <div className="cal-list">
      {groups.map(([key, dayItems]) => {
        const d = parseDate(key);
        const isT = isSameDay(d, new Date());
        return (
          <div key={key} className="cal-list-group">
            <div className={`cal-list-date${isT ? " today" : ""}`}>{fmtDayFull(d)}</div>
            <div className="cal-list-items">
              {dayItems.map(item => (
                <button key={item.id} className="cal-list-row" onClick={e => onItemClick(item, e)} style={{ borderLeft: `3px solid ${item.color.border}` }}>
                  <span className="cal-list-dot" style={{ background: item.color.bg, border: `1.5px solid ${item.color.border}` }} />
                  <span className="cal-list-time">{item.allDay ? "Journée" : (item.time || "")}</span>
                  <span className="cal-list-title">{item.title}</span>
                  <span className="cal-list-tail">
                    {item.clubs.map(c => <span key={c.id} className="cal-list-club" style={{ background: (c.color || "#94A3B8") + "1A", color: c.color || "var(--text-2)" }}>{c.name}</span>)}
                    <span className="cal-list-type" style={{ background: item.color.bg, color: item.color.text }}>{SOURCE_LABELS[item.sourceType]}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Timed event block ── */
function TimedEvent({ item, onClick }) {
  const startMin = timeToMins(item.time) ?? (HOUR_START * 60);
  const endMin   = item.endTime ? timeToMins(item.endTime) : startMin + 60;
  const top      = ((startMin / 60) - HOUR_START) * HOUR_H;
  const height   = Math.max(((endMin - startMin) / 60) * HOUR_H, 22);
  return (
    <button className="cal-timed" style={{ top, height, background: item.color.bg, color: item.color.text, borderLeft: `3px solid ${item.color.border}` }} onClick={onClick}>
      <span className="cal-timed-time">{item.time}</span>
      <span className="cal-timed-title">{item.title}</span>
    </button>
  );
}

/* ── Current time line ── */
function NowLine() {
  const [top, setTop] = useState(() => ((new Date().getHours() * 60 + new Date().getMinutes()) / 60 - HOUR_START) * HOUR_H);
  useEffect(() => {
    const id = setInterval(() => {
      const now = new Date();
      setTop(((now.getHours() * 60 + now.getMinutes()) / 60 - HOUR_START) * HOUR_H);
    }, 60000);
    return () => clearInterval(id);
  }, []);
  if (top < 0 || top > (HOUR_END - HOUR_START) * HOUR_H) return null;
  return <div className="cal-now-line" style={{ top }} />;
}

/* ════════════════════════════════════════════════════════
   POPOVER — fiche détaillée (jamais d'ID technique affiché)
   ════════════════════════════════════════════════════════ */
const ItemPopover = React.forwardRef(function ItemPopover(
  { item, x, y, users, campagnes, onNavigate, onEditCommEvent, canEditCommEvent, onClose }, ref
) {
  const { sourceType, title, date, endDate, time, endTime, allDay, clubs, status, meta } = item;
  const col = item.color;

  const fmtDate = d => d ? `${FR_DAYS[(d.getDay()+6)%7]} ${d.getDate()} ${FR_MONTHS[d.getMonth()].slice(0,3)} ${d.getFullYear()}` : null;
  const dateStr    = fmtDate(date);
  const endDateStr = fmtDate(endDate);

  const timeStr = allDay ? "Toute la journée" : time ? (endTime ? `${time} – ${endTime}` : time) : null;
  const whenLine = endDateStr
    ? `${dateStr} → ${endDateStr}`
    : timeStr ? (dateStr ? `${dateStr}  ·  ${timeStr}` : timeStr) : dateStr;

  const assignedName    = resolveUser(meta.assignedTo || meta.assignee, users) || resolveUserList(meta.assignedTo, users);
  const ownerName       = resolveUser(meta.owner || meta.responsable || meta.createdBy, users);
  const participantList = resolveUserList(meta.participants || meta.members, users);

  const W = typeof window !== "undefined" ? window.innerWidth  : 900;
  const H = typeof window !== "undefined" ? window.innerHeight : 700;
  const safeX = Math.min(x, W - 308);
  const safeY = Math.min(y, H - 420);

  const details = [];
  if (sourceType === "task") {
    if (assignedName) details.push({ label: "Assigné à", value: assignedName });
    if (meta.project) details.push({ label: "Projet", value: meta.project });
  }
  if (sourceType === "publication") {
    if (meta.platform) details.push({ label: "Plateforme", value: meta.platform });
    if (meta.type)     details.push({ label: "Format", value: meta.type });
    if (meta.campagneId) {
      const campName = (campagnes || []).find(c => String(c.id) === String(meta.campagneId))?.name;
      if (campName) details.push({ label: "Campagne", value: campName });
    }
    if (meta.networks) details.push({ label: "Réseaux", value: Array.isArray(meta.networks) ? meta.networks.join(", ") : meta.networks });
  }
  if (sourceType === "event") {
    if (meta.location) details.push({ label: "Lieu", value: meta.location });
    if (ownerName)     details.push({ label: "Créé par", value: ownerName });
  }
  if (sourceType === "commevent") {
    if (ownerName) details.push({ label: "Responsable", value: ownerName });
  }
  if (sourceType === "meeting") {
    if (meta.location)   details.push({ label: "Lieu", value: meta.location });
    if (meta.link)       details.push({ label: "Lien", value: meta.link });
    if (participantList) details.push({ label: "Participants", value: participantList });
  }
  if (sourceType === "project") {
    if (ownerName)       details.push({ label: "Responsable", value: ownerName });
    if (participantList) details.push({ label: "Équipe", value: participantList });
  }
  if (sourceType === "request") {
    if (assignedName) details.push({ label: "Assigné à", value: assignedName });
  }
  if (sourceType === "tournoi") {
    if (meta.level) details.push({ label: "Niveau", value: meta.level });
    const cat = [meta.category, meta.ageCategory].filter(Boolean).join(" · ");
    if (cat) details.push({ label: "Catégorie", value: cat });
    if (meta.location) details.push({ label: "Lieu", value: meta.location });
    if (meta.maxPairs != null)
      details.push({ label: "Inscriptions", value: `${meta.confirmedPairs ?? meta.registeredPairs ?? 0} / ${meta.maxPairs} paires` });
  }

  const excerpt = sourceType === "metricool" ? meta.text
    : sourceType === "project" ? meta.presentation || meta.concept
    : sourceType === "request" ? meta.description
    : sourceType === "commevent" ? meta.description
    : null;

  const targetPage = ROUTE_MAP[sourceType] ?? null;

  return (
    <div ref={ref} className="cal-popover" style={{ left: safeX, top: safeY, borderTop: `3px solid ${col.border}` }}>
      <div className="cal-pop-bar">
        <span className="cal-pop-source" style={{ background: col.bg, color: col.text }}>
          {SOURCE_LABELS[sourceType] || sourceType}
        </span>
        <button className="cal-pop-close" onClick={onClose} aria-label="Fermer">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1.5 1.5l9 9M10.5 1.5l-9 9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
        </button>
      </div>

      <div className="cal-pop-body">
        <div className="cal-pop-title">{title}</div>
        {whenLine && <div className="cal-pop-when">{whenLine}</div>}

        {(clubs.length > 0 || status) && (
          <div className="cal-pop-meta-row">
            {clubs.map(c => (
              <span key={c.id} className="cal-pop-club">
                <span className="cal-pop-club-dot" style={{ background: c.color || col.border }} />
                {c.name}
              </span>
            ))}
            {status && <span className="cal-pop-status" style={{ background: col.bg, color: col.text }}>{status}</span>}
          </div>
        )}

        {details.length > 0 && (
          <>
            <div className="cal-pop-sep" />
            {details.map(({ label, value }) => (
              <div key={label} className="cal-pop-row">
                <span className="cal-pop-lbl">{label}</span>
                <span className="cal-pop-val">{value}</span>
              </div>
            ))}
          </>
        )}

        {excerpt && (
          <div className="cal-pop-excerpt">{String(excerpt).slice(0, 130)}{String(excerpt).length > 130 ? "…" : ""}</div>
        )}

        {sourceType === "commevent" && canEditCommEvent && (
          <>
            <div className="cal-pop-sep" />
            <button className="cal-pop-action" onClick={() => onEditCommEvent(meta)}>
              Modifier l'événement
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M3 7h8M8 4l3 3-3 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          </>
        )}

        {sourceType === "tournoi" && (
          <>
            <div className="cal-pop-sep" />
            <a className="cal-pop-action" href="https://tournois.espritpadel.com/tournois.html" target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
              Voir sur l'app Tournois
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M3 7h8M8 4l3 3-3 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </a>
          </>
        )}

        {targetPage && onNavigate && (
          <>
            <div className="cal-pop-sep" />
            <button className="cal-pop-action" onClick={() => { onClose(); onNavigate(targetPage); }}>
              Ouvrir la fiche
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M3 7h8M8 4l3 3-3 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          </>
        )}
      </div>
    </div>
  );
});

/* ════════════════════════════════════════════════════════
   CREATION — sélecteur de type + formulaires
   ════════════════════════════════════════════════════════ */
const CREATE_TYPES = [
  { key: "commevent",   label: "Événement ponctuel", icon: "📌", color: C.commevent },
  { key: "task",        label: "Tâche",              icon: "✓",  color: C.task },
  { key: "meeting",     label: "Réunion",            icon: "👥", color: C.meeting },
  { key: "publication", label: "Publication",        icon: "📣", color: C.publication },
];

function CreateModal({
  clubs, users, campagnes, isAdmin, defaultClubs, defaultDate, canCreateEvent,
  onClose, onCreateCommEvent, onCreateTask, onCreateMeeting, onCreatePublication,
}) {
  const [type, setType] = useState(null);
  const available = CREATE_TYPES.filter(t => t.key !== "commevent" || canCreateEvent);

  if (!type) {
    return (
      <Overlay onClose={onClose}>
        <h3 className="cal-modal-title">Créer un élément</h3>
        <p className="cal-modal-sub">Choisissez le type d'élément à ajouter au calendrier.</p>
        <div className="cal-type-grid">
          {available.map(t => (
            <button key={t.key} className="cal-type-card" onClick={() => setType(t.key)} style={{ borderColor: t.color.border }}>
              <span className="cal-type-icon" style={{ background: t.color.bg, color: t.color.text }}>{t.icon}</span>
              <span className="cal-type-label">{t.label}</span>
            </button>
          ))}
        </div>
        <div className="cal-modal-actions"><button className="cal-mbtn-ghost" onClick={onClose}>Annuler</button></div>
      </Overlay>
    );
  }

  const common = { clubs, users, campagnes, isAdmin, defaultClubs, defaultDate, onClose, onBack: () => setType(null) };
  if (type === "commevent")   return <CommEventForm   {...common} onSave={onCreateCommEvent} />;
  if (type === "task")        return <TaskForm        {...common} onSave={onCreateTask} />;
  if (type === "meeting")     return <MeetingForm     {...common} onSave={onCreateMeeting} />;
  if (type === "publication") return <PublicationForm {...common} onSave={onCreatePublication} />;
  return null;
}

/* Sélecteur de clubs multiple (ou mono pour événement ponctuel). */
function ClubPicker({ clubs, value, onChange, single }) {
  if (!clubs.length) return <div className="cal-hint">Aucun club disponible.</div>;
  if (single) {
    return (
      <select className="cal-input" value={value[0] || ""} onChange={e => onChange(e.target.value ? [e.target.value] : [])}>
        <option value="">Tous clubs (national)</option>
        {clubs.map(c => <option key={c.id} value={c.id}>{c.name || c.nom}</option>)}
      </select>
    );
  }
  return (
    <div className="cal-checklist">
      {clubs.map(c => {
        const on = value.map(String).includes(String(c.id));
        return (
          <label key={c.id} className="cal-check-item">
            <input type="checkbox" checked={on} onChange={() => onChange(on ? value.filter(v => String(v) !== String(c.id)) : [...value, c.id])} />
            <span className="cal-filter-dot" style={{ background: c.color || "var(--text-3)" }} />
            <span>{c.name || c.nom}</span>
          </label>
        );
      })}
    </div>
  );
}

/* Sélecteur multiple d'utilisateurs. */
function UserPicker({ users, value, onChange }) {
  const list = (users || []).filter(u => u && (u.name || u.firstName || u.email));
  if (!list.length) return <div className="cal-hint">Aucun utilisateur.</div>;
  return (
    <div className="cal-checklist cal-checklist--scroll">
      {list.map(u => {
        const label = u.name || [u.firstName, u.lastName].filter(Boolean).join(" ") || u.email;
        const on = value.map(String).includes(String(u.id));
        return (
          <label key={u.id} className="cal-check-item">
            <input type="checkbox" checked={on} onChange={() => onChange(on ? value.filter(v => String(v) !== String(u.id)) : [...value, u.id])} />
            <span>{label}</span>
          </label>
        );
      })}
    </div>
  );
}

function CommEventForm({ clubs, defaultClubs, defaultDate, onSave, onClose, onBack }) {
  const [f, setF] = useState({ title: "", date: defaultDate, time: "", endTime: "", clubId: defaultClubs[0] || "", description: "" });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const clubName = (id) => (clubs.find(c => String(c.id) === String(id))?.name) || "";
  const submit = () => { if (!f.title.trim() || !f.date) return; onSave({ ...f, title: f.title.trim(), clubName: f.clubId ? clubName(f.clubId) : "" }); };
  return (
    <Overlay onClose={onClose}>
      <FormHeader title="Nouvel événement ponctuel" onBack={onBack} />
      <Field label="Titre"><input autoFocus className="cal-input" value={f.title} onChange={e => set("title", e.target.value)} placeholder="Ex : Shooting photo, RDV presse…" /></Field>
      <Row>
        <Field label="Date"><input type="date" className="cal-input" value={f.date} onChange={e => set("date", e.target.value)} /></Field>
        <Field label="Club"><ClubPicker single clubs={clubs} value={f.clubId ? [f.clubId] : []} onChange={v => set("clubId", v[0] || "")} /></Field>
      </Row>
      <Row>
        <Field label="Heure début"><input type="time" className="cal-input" value={f.time} onChange={e => set("time", e.target.value)} /></Field>
        <Field label="Heure fin"><input type="time" className="cal-input" value={f.endTime} onChange={e => set("endTime", e.target.value)} /></Field>
      </Row>
      <Field label="Description"><textarea className="cal-input" rows={3} value={f.description} onChange={e => set("description", e.target.value)} /></Field>
      <SubmitRow onClose={onClose} onSubmit={submit} />
    </Overlay>
  );
}

function TaskForm({ clubs, users, defaultClubs, defaultDate, onSave, onClose, onBack }) {
  const [f, setF] = useState({ title: "", deadline: defaultDate, time: "", clubs: defaultClubs, assignedTo: [], urgency: "Normal", description: "" });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const submit = () => { if (!f.title.trim() || !f.deadline) return; onSave(f); };
  return (
    <Overlay onClose={onClose}>
      <FormHeader title="Nouvelle tâche" onBack={onBack} />
      <Field label="Titre"><input autoFocus className="cal-input" value={f.title} onChange={e => set("title", e.target.value)} placeholder="Ex : Préparer le visuel…" /></Field>
      <Row>
        <Field label="Échéance"><input type="date" className="cal-input" value={f.deadline} onChange={e => set("deadline", e.target.value)} /></Field>
        <Field label="Urgence">
          <select className="cal-input" value={f.urgency} onChange={e => set("urgency", e.target.value)}>
            {["Normal","Basse","Haute"].map(u => <option key={u} value={u}>{u}</option>)}
          </select>
        </Field>
      </Row>
      <Field label="Club(s)"><ClubPicker clubs={clubs} value={f.clubs} onChange={v => set("clubs", v)} /></Field>
      <Field label="Assigné à"><UserPicker users={users} value={f.assignedTo} onChange={v => set("assignedTo", v)} /></Field>
      <Field label="Description"><textarea className="cal-input" rows={2} value={f.description} onChange={e => set("description", e.target.value)} /></Field>
      <SubmitRow onClose={onClose} onSubmit={submit} />
    </Overlay>
  );
}

function MeetingForm({ clubs, users, defaultClubs, defaultDate, onSave, onClose, onBack }) {
  const [f, setF] = useState({ title: "", date: defaultDate, time: "10:00", endTime: "", location: "", type: "Présentiel", clubs: defaultClubs, participants: [], link: "", notesBefore: "" });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const submit = () => { if (!f.title.trim() || !f.date) return; onSave(f); };
  return (
    <Overlay onClose={onClose}>
      <FormHeader title="Nouvelle réunion" onBack={onBack} />
      <Field label="Titre"><input autoFocus className="cal-input" value={f.title} onChange={e => set("title", e.target.value)} placeholder="Ex : Point hebdo comm…" /></Field>
      <Row>
        <Field label="Date"><input type="date" className="cal-input" value={f.date} onChange={e => set("date", e.target.value)} /></Field>
        <Field label="Type">
          <select className="cal-input" value={f.type} onChange={e => set("type", e.target.value)}>
            {["Présentiel","Visio","Téléphone"].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
      </Row>
      <Row>
        <Field label="Heure début"><input type="time" className="cal-input" value={f.time} onChange={e => set("time", e.target.value)} /></Field>
        <Field label="Heure fin"><input type="time" className="cal-input" value={f.endTime} onChange={e => set("endTime", e.target.value)} /></Field>
      </Row>
      <Row>
        <Field label="Lieu"><input className="cal-input" value={f.location} onChange={e => set("location", e.target.value)} placeholder="Salle, adresse…" /></Field>
        <Field label="Lien visio"><input className="cal-input" value={f.link} onChange={e => set("link", e.target.value)} placeholder="https://…" /></Field>
      </Row>
      <Field label="Club(s)"><ClubPicker clubs={clubs} value={f.clubs} onChange={v => set("clubs", v)} /></Field>
      <Field label="Participants"><UserPicker users={users} value={f.participants} onChange={v => set("participants", v)} /></Field>
      <SubmitRow onClose={onClose} onSubmit={submit} />
    </Overlay>
  );
}

function PublicationForm({ clubs, campagnes, defaultClubs, defaultDate, onSave, onClose, onBack }) {
  const [f, setF] = useState({ title: "", date: defaultDate, platform: "Instagram", type: "Post", status: "Brouillon", campagneId: "", clubs: defaultClubs, notes: "" });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const submit = () => { if (!f.title.trim() || !f.date) return; onSave(f); };
  return (
    <Overlay onClose={onClose}>
      <FormHeader title="Nouvelle publication" onBack={onBack} />
      <Field label="Titre"><input autoFocus className="cal-input" value={f.title} onChange={e => set("title", e.target.value)} placeholder="Ex : Reel montage pergola…" /></Field>
      <Row>
        <Field label="Date"><input type="date" className="cal-input" value={f.date} onChange={e => set("date", e.target.value)} /></Field>
        <Field label="Statut">
          <select className="cal-input" value={f.status} onChange={e => set("status", e.target.value)}>
            {["Brouillon","À valider","Validé","Publié"].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
      </Row>
      <Row>
        <Field label="Plateforme">
          <select className="cal-input" value={f.platform} onChange={e => set("platform", e.target.value)}>
            {["Instagram","Facebook","TikTok","LinkedIn","X","YouTube"].map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </Field>
        <Field label="Format">
          <select className="cal-input" value={f.type} onChange={e => set("type", e.target.value)}>
            {["Post","Reel","Story","Carrousel","Vidéo"].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
      </Row>
      <Field label="Campagne (optionnel)">
        <select className="cal-input" value={f.campagneId} onChange={e => set("campagneId", e.target.value)}>
          <option value="">— Aucune —</option>
          {(campagnes || []).filter(c => c.name).map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
        </select>
      </Field>
      <Field label="Club(s)"><ClubPicker clubs={clubs} value={f.clubs} onChange={v => set("clubs", v)} /></Field>
      <Field label="Notes"><textarea className="cal-input" rows={2} value={f.notes} onChange={e => set("notes", e.target.value)} /></Field>
      <SubmitRow onClose={onClose} onSubmit={submit} />
    </Overlay>
  );
}

/* Modale d'édition d'un événement ponctuel existant. */
function EventModal({ initial, clubs, onClose, onSave, onDelete, canDelete }) {
  const [f, setF] = useState({ title: "", date: "", time: "", endTime: "", clubId: "", description: "", ...initial });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const clubName = (id) => (clubs.find(c => String(c.id) === String(id))?.name) || "";
  const submit = () => { if (!f.title.trim() || !f.date) return; onSave({ ...f, title: f.title.trim(), clubName: f.clubId ? clubName(f.clubId) : "" }); };
  return (
    <Overlay onClose={onClose}>
      <h3 className="cal-modal-title">Modifier l'événement</h3>
      <Field label="Titre"><input autoFocus className="cal-input" value={f.title} onChange={e => set("title", e.target.value)} /></Field>
      <Row>
        <Field label="Date"><input type="date" className="cal-input" value={f.date} onChange={e => set("date", e.target.value)} /></Field>
        <Field label="Club"><ClubPicker single clubs={clubs} value={f.clubId ? [f.clubId] : []} onChange={v => set("clubId", v[0] || "")} /></Field>
      </Row>
      <Row>
        <Field label="Heure début"><input type="time" className="cal-input" value={f.time} onChange={e => set("time", e.target.value)} /></Field>
        <Field label="Heure fin"><input type="time" className="cal-input" value={f.endTime} onChange={e => set("endTime", e.target.value)} /></Field>
      </Row>
      <Field label="Description"><textarea className="cal-input" rows={3} value={f.description} onChange={e => set("description", e.target.value)} /></Field>
      <div className="cal-modal-actions cal-modal-actions--split">
        <div>{canDelete && <button className="cal-mbtn-danger" onClick={() => onDelete(f.id)}>🗑️ Supprimer</button>}</div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="cal-mbtn-ghost" onClick={onClose}>Annuler</button>
          <button className="cal-mbtn-primary" onClick={submit}>Enregistrer</button>
        </div>
      </div>
    </Overlay>
  );
}

/* Modale autorisations (qui peut créer des événements ponctuels). */
function PermModal({ users, editors, onToggle, onClose }) {
  const ed = new Set((editors || []).map(String));
  const list = (users || []).filter(u => u.email);
  return (
    <Overlay onClose={onClose}>
      <h3 className="cal-modal-title">Qui peut créer des événements</h3>
      <p className="cal-modal-sub">Les administrateurs peuvent toujours en créer. Cochez les autres personnes autorisées.</p>
      <div className="cal-checklist cal-checklist--scroll">
        {list.map(u => {
          const admin = u.admin === true;
          const on = admin || ed.has(String(u.id));
          return (
            <label key={u.id} className="cal-check-item">
              <input type="checkbox" checked={on} disabled={admin} onChange={() => onToggle(u.id)} />
              <span>{u.name || [u.firstName, u.lastName].filter(Boolean).join(" ") || u.email}</span>
              {admin && <span className="cal-filter-badge" style={{ marginLeft: "auto" }}>ADMIN</span>}
            </label>
          );
        })}
      </div>
      <div className="cal-modal-actions"><button className="cal-mbtn-primary" onClick={onClose}>Fermer</button></div>
    </Overlay>
  );
}

/* ── Primitives de formulaire ── */
function Overlay({ children, onClose }) {
  return (
    <div className="cal-overlay" onClick={onClose}>
      <div className="cal-modal" onClick={e => e.stopPropagation()}>{children}</div>
    </div>
  );
}
function FormHeader({ title, onBack }) {
  return (
    <div className="cal-form-header">
      <button className="cal-back-btn" onClick={onBack} aria-label="Retour">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </button>
      <h3 className="cal-modal-title" style={{ margin: 0 }}>{title}</h3>
    </div>
  );
}
function Field({ label, children }) {
  return <div className="cal-field"><label className="cal-field-label">{label}</label>{children}</div>;
}
function Row({ children }) { return <div className="cal-form-row">{children}</div>; }
function SubmitRow({ onClose, onSubmit }) {
  return (
    <div className="cal-modal-actions">
      <button className="cal-mbtn-ghost" onClick={onClose}>Annuler</button>
      <button className="cal-mbtn-primary" onClick={onSubmit}>Enregistrer</button>
    </div>
  );
}
